import { graphql } from 'react-relay';
import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type { ContentStateRestrictionLevel } from '../../entry-points/EditorContentStateQuery.ts';
import { PRIMARY_COLORS } from '../colors.ts';
import { ContentStateColor } from '../ContentStateColor.tsx';

import type {
	RestoreDefaultsTextButtonResetSpaceContentStatesMutation as RestoreDefaultsTextButtonResetSpaceContentStatesMutation$data,
	RestoreDefaultsTextButtonResetSpaceContentStatesMutationVariables as RestoreDefaultsTextButtonResetSpaceContentStatesMutation$variables,
} from './__types__/RestoreDefaultsTextButtonResetSpaceContentStatesMutation.ts';
import type { ContentState, RestoreDefaultsTextButtonProps } from './types.ts';
import { i18n } from './i18n.ts';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnderlinedBodyTextWrapper = styled.span({
	font: token('font.body.small'),
	color: token('color.text.subtle'),
	textDecoration: 'underline',
	cursor: 'pointer',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterButtonsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateWrapper = styled.div({
	display: 'flex',
	marginBottom: token('space.250'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CircleWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginRight: token('space.250'),
});
const restoreDefaultButtonWrapperStyles = xcss({
	marginLeft: 'space.025',
});

type DefaultStateData = {
	color: string;
	name: string;
	restrictionLevel: ContentStateRestrictionLevel;
};

const RestoreDefaultsTextButtonComponent = ({
	spaceKey,
	underlinedBodyText,
	originalSuggestedStatesArray,
	setSuggestedStatesArray,
	intl: { formatMessage },
}: RestoreDefaultsTextButtonProps) => {
	const [restoreDefaultStatesMutation] = useMutationInterop<
		RestoreDefaultsTextButtonResetSpaceContentStatesMutation$data,
		RestoreDefaultsTextButtonResetSpaceContentStatesMutation$variables
	>(
		graphql`
			mutation RestoreDefaultsTextButtonResetSpaceContentStatesMutation($spaceKey: String!) {
				resetSpaceContentStates(spaceKey: $spaceKey) {
					contentStatesAllowed
					spaceContentStatesAllowed
					customContentStatesAllowed
					spaceContentStates {
						name
						color
						id
						restrictionLevel
					}
				}
			}
		`,
		{
			onCompleted: (data) => {
				setSuggestedStatesArray(
					[...(data?.resetSpaceContentStates?.spaceContentStates || [])].map(
						(s): ContentState | null => s ?? null,
					),
				); // need to set it here since we need the id's we get back from BE
			},
		},
	);
	const { Modal } = useChoreographerAssets();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const roughDraft: DefaultStateData = {
		color: PRIMARY_COLORS.YELLOW,
		name: formatMessage(i18n.roughDraft),
		restrictionLevel: 'NONE',
	};
	const inProgress: DefaultStateData = {
		color: PRIMARY_COLORS.BLUE,
		name: formatMessage(i18n.inProgress),
		restrictionLevel: 'NONE',
	};
	const readyForReview: DefaultStateData = {
		color: PRIMARY_COLORS.GREEN,
		name: formatMessage(i18n.readyForReview),
		restrictionLevel: 'NONE',
	};
	const verified: DefaultStateData = {
		color: PRIMARY_COLORS.VERIFIED,
		name: formatMessage(i18n.verified),
		restrictionLevel: 'PAGE_OWNER',
	};
	const defaultStatesArray = [roughDraft, inProgress, readyForReview, verified];
	const defaultStates = defaultStatesArray.map((state) => {
		return (
			<ContentStateWrapper key={state.name}>
				<CircleWrapper>
					<ContentStateColor
						dataTestId="color-circle"
						appearance="restoreContentStatesModal"
						color={state.color}
						restrictionLevel={state.restrictionLevel}
					/>
				</CircleWrapper>
				{state.name}
			</ContentStateWrapper>
		);
	});
	const onClickRestoreDefaultsText = () => {
		setIsModalOpen(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'text',
				objectId: spaceKey,
				actionSubjectId: 'adminClickedRestoreDefaultsText',
				source: 'contentStateAdminSettings',
			},
		}).fire();
	};
	const closeModal = () => {
		setIsModalOpen(false);
	};
	const restoreDefaults = () => {
		void restoreDefaultStatesMutation({
			variables: {
				spaceKey,
			},
		});
		setSuggestedStatesArray!(originalSuggestedStatesArray);
		closeModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: spaceKey,
				actionSubjectId: 'adminConfirmedRestoreDefaults',
				source: 'contentStateAdminSettings',
			},
		}).fire();
	};
	return (
		<Box xcss={restoreDefaultButtonWrapperStyles}>
			<UnderlinedBodyTextWrapper
				onClick={onClickRestoreDefaultsText}
				data-testid="restore-defaults-button"
			>
				{underlinedBodyText}
			</UnderlinedBodyTextWrapper>
			<ModalTransition>
				{isModalOpen && (
					<Modal
						testId="restore-defaults-modal"
						onClose={closeModal}
						width="small"
						messageType="transactional"
						messageId="content-state-restore-defaults-modal"
					>
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(i18n.restoreDefaultSuggestions)}</ModalTitle>
						</ModalHeader>
						<ModalBody>{defaultStates}</ModalBody>
						<ModalFooter>
							<FooterButtonsWrapper>
								<Button
									testId="cancel-button"
									appearance="subtle"
									onClick={closeModal}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={{ marginRight: token('space.100') }}
								>
									{formatMessage(i18n.cancel)}
								</Button>
								<Button
									testId="confirm-button"
									appearance="primary"
									onClick={restoreDefaults}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={{ marginRight: token('space.250') }}
								>
									{formatMessage(i18n.confirm)}
								</Button>
							</FooterButtonsWrapper>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</Box>
	);
};
export const RestoreDefaultsTextButton = injectIntl(RestoreDefaultsTextButtonComponent);
